import React, { useState, useRef, useEffect } from 'react';
import { debounce, cloneDeep, set } from 'lodash';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faTimes } from '@fortawesome/pro-light-svg-icons';
import generateReactKey from '../../../../services/generateReactKey';
import SubmittableSearchBar from '../../../../common-client2/src/components/SubmittableSearchBar';
import { dynamicSort } from '../../../../common-client2/src/services/sort';
import { Paper, CircularProgress } from '@mui/material';
import { Table } from 'rsuite';
import { RequestTypes } from '../../../Enums';
import ReactDateRangePicker from "../../../../common/components/UI/Datepicker/ReactDateRangePicker";
import { formatDateRange, getAsLongDate } from '../../../../common-client2/src/services/ukgDateTime';
import moment from 'moment';
import { OfferCompliance, OfferDetails } from './OfferComponents';

import './AllEmployeeOffersReport.scss';

import {
    mapToAllEmployeeOffersReportTableEntries,
    initialStateSortValues,
    requestTypeList,
    rowKey,
    mapSymbolicPeriodToRange,
    convertToUserExactDate,
    mapPeriodsWithlabel,
} from './AllEmployeeOffersReport.service';
import { getAllEmployeeOffersReport, getAllEmployeeOfferValidations } from '../../EmployeeDashboard/Utils/dotNet';
import { MultipleReactSelect as ReactMultipleSelect } from '../../../../common/components/UI/Select';
import { getSymbolicPeriodList } from '../../../services/api-endpoints';
import { isMobile } from 'react-device-detect';

const { Column, HeaderCell, Cell } = Table;
const defaultPeriodSelectedSymbolicId = "Current_Week";

/** @type {Object<string, import('moment').Moment[]>} */
const initialSymbolicPeriodList = {};

/** @param {import('./AllEmployeeOffersReport.service').AllEmployeeOffersReportProps} param0 */
const AllEmployeeOffersReport = ({
    data = [],
}) => {
    const { t } = useTranslation('employmentBillboard');
    const { t: tReport } = useTranslation('employmentBillboard', { keyPrefix: 'billboard.allEmployeeReports' });

    data = mapToAllEmployeeOffersReportTableEntries([]);
    const ref = useRef(null);
    let promise1 = null;

    // Needs to be controlled.
    const [symbolicPeriodRange, setSymbolicPeriodRange] = useState(initialSymbolicPeriodList);
    const [selectedSymbolicPeriod, setSelectedSymbolicPeriod] = useState();
    const [periods, setPeriods] = useState([]);

    const [requestTypes, setRequestTypes] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [sortValues, setSortValues] = useState(initialStateSortValues);
    const [fullData, setFullData] = useState(data);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [showDetails, setShowDetails] = useState(false);

    const handleCloseDetails = () => {
        setSelectedRow(null);
        setShowDetails(false);
      };

    useEffect(() => {
        if(selectedRow !== null){
            const selectedRowDataWithValidations = fullData.find(x => selectedRow.offerId === x.offerId);            
            setSelectedRow({...selectedRowDataWithValidations});
        }
    }, [fullData]);


    const debouncedLoadReportData = useRef(
        debounce(async (params) => {
            setIsLoading(true);
            const local = promise1 = getAllEmployeeOffersReport(params).then(({ data: offerResults }) => {
                if (local !== promise1) {
                    throw "Error: new promise started. Aborting previous promise";
                } 
                var mappedData = mapToAllEmployeeOffersReportTableEntries(offerResults);
                setFullData(mappedData);
                getAllEmployeeOfferValidations(params).then(({ data: validationResults }) => {
                    if (local !== promise1) {
                        throw "Error: new promise started. Aborting previous promise";
                    }
                    const mappedDataWithValidations = mappedData.map(x => {
                        let validation = null;
                        switch (x.requestType) {
                            case RequestTypes.toCover:
                            case RequestTypes.shiftSwap:
                                validation = validationResults.find(y =>
                                    y.requestType === x.requestType
                                    && y.managerSettingAggregateRootId === x.managerSettingAggregateRootId
                                    && y.offerId === x.offerId
                                );
                                break;
                            case RequestTypes.openShift:
                                validation = validationResults.find(y =>
                                    y.requestType === RequestTypes.openShift
                                    && y.managerSettingAggregateRootId === x.managerSettingAggregateRootId
                                    && y.openShiftKey === x.openShiftKey
                                );
                                break;
                            case RequestTypes.voluntaryTimeOff:
                                validation = validationResults.find(y =>
                                    y.requestType === RequestTypes.voluntaryTimeOff
                                    && y.managerSettingAggregateRootId === x.managerSettingAggregateRootId
                                );
                                break;
                            default:
                                break;
                        }

                        if (validation) {
                            x.rulesValidationComplicance = validation.isValid;
                            return x;
                        }
                        return x;
                    });
                    setFullData(mappedDataWithValidations);
                });
            }).finally(() => {
                setIsLoading(false);
            });
        }, 2000)
    ).current;

    useEffect(() => {
        return () => {
            debouncedLoadReportData.cancel();
        };
    }, [debouncedLoadReportData]);

    useEffect(() => {
        getSymbolicPeriodList().then(({ data }) => {
            const periodsWithLabel = mapPeriodsWithlabel(data);
            setSymbolicPeriodRange(mapSymbolicPeriodToRange(periodsWithLabel));
            setPeriods(periodsWithLabel);
            setSelectedSymbolicPeriod(periodsWithLabel.find((x) => x.symbolicId === defaultPeriodSelectedSymbolicId));
        });
    }, []);

    useEffect(() => {
        const sortKey = sortValues.sortType === 'desc' ? `-${sortValues.sortColumn}` : sortValues.sortColumn;
        var sortedData = cloneDeep(fullData?.sort(dynamicSort(sortKey)));
        setFullData(sortedData);
    }, [sortValues.sortType, sortValues.sortColumn]);

    useEffect(() => {
        if (!selectedSymbolicPeriod || !requestTypes.length) {
            setFullData([]);
            return;
        }

        fetchReports();
    }, [JSON.stringify(selectedSymbolicPeriod), JSON.stringify(requestTypes)]);

    const fetchReports = () => {
        if (!selectedSymbolicPeriod || !requestTypes.length) {
            return;
        }
        const params = {
            startDate: selectedSymbolicPeriod.startDate,
            endDate: selectedSymbolicPeriod.endDate,
            requestTypes: requestTypes.map((x) => x.value),
        };
        debouncedLoadReportData(params);
    };

    const onApplyPicker = (_, picker) => {
        let foundSelectedPeriod = periods.find((element) => element.name === picker.chosenLabel);

        if (foundSelectedPeriod === undefined && picker.chosenLabel === "Custom Range") {
            const startDate = moment(picker.startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss');
            const endDate = moment(picker.endDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss');
            const label = formatDateRange(moment(startDate).toDate(), moment(endDate).toDate(), getAsLongDate);
            foundSelectedPeriod = {
                startDate: startDate,
                endDate: endDate,
                label: label,
                value: label,
                name: label
            }
        }
        setSelectedSymbolicPeriod(foundSelectedPeriod);
    }

    let filteredData = fullData;
    if (searchValue) {
        filteredData = fullData.filter(x => x.searchValue.includes(searchValue.toLocaleLowerCase()));
    }

    return (
        <div className="all-employee-offers-report">
            <div className='container-filters'>
                <div className='filters'>
                    {
                        !!Object.keys(symbolicPeriodRange).length && (
                            <>
                                <ReactDateRangePicker
                                    key={generateReactKey()}
                                    ranges={symbolicPeriodRange}
                                    startDate={convertToUserExactDate(selectedSymbolicPeriod?.startDate)}
                                    endDate={convertToUserExactDate(selectedSymbolicPeriod?.endDate)}
                                    onApply={onApplyPicker}
                                    periodLabel={selectedSymbolicPeriod?.label}
                                    customRangeLabel={'Custom Range'}
                                    disableTranslations
                                />
                            </>
                        )
                    }
                    <ReactMultipleSelect
                        labelText={t('billboard.selectRequestType')}
                        value={requestTypes}
                        onChange={(_, value) => {
                            setRequestTypes([...value]);
                        }}
                        optionsList={requestTypeList}
                        valueStateName="requestTypes"
                    />
                </div>
                <div className="searchbar">
                    <SubmittableSearchBar
                        isTriggerSearchOnChange={false}
                        onChange={(value) => setSearchValue(value)}
                    />
                </div>
            </div>
            <Paper
                elevation={2}
                className="all-employee-report-table-container"
                ref={ref}
            >
                <Table
                    sortColumn={sortValues.sortColumn}
                    sortType={sortValues.sortType}
                    onSortColumn={(sortColumn, sortType) => setSortValues({ sortColumn, sortType })}
                    fillHeight
                    data={filteredData}
                    id="table-all-employee-offers-report"
                    className="table-all-employee-offers-report"
                    rowKey={rowKey}
                    shouldUpdateScroll={false}
                    wordWrap
                    locale={{ emptyMessage: tReport('noReportFound') }}
                    virtualized
                    affixHorizontalScrollbar
                    loading={isLoading}
                    style={{ width: '100%' }}
                    onRowClick={(rowData) => {
                        setSelectedRow({ ...rowData });
                        setShowDetails(true);
                    }}

                >
                    <Column
                        flexGrow={1}
                        align="center"
                        key="requestName"
                        sortable
                    >
                        <HeaderCell>{tReport('request')}</HeaderCell>
                        <Cell dataKey="requestName">
                            {
                                (/** @type {import('./AllEmployeeOffersReport.service').AllEmployeeOffersReportEntry} */ { requestName }) => (
                                    <>
                                        <span className="text-md-semibold">{requestName}</span>
                                    </>
                                )
                            }
                        </Cell>
                    </Column>
                    <Column
                        flexGrow={0.8}
                        align="center"
                        key="requestType"
                        sortable
                    >
                        <HeaderCell>
                            {isMobile ? tReport('typeShort') : tReport('requestType')}
                        </HeaderCell>
                        <Cell dataKey="requestType">
                            {
                                (/** @type {import('./AllEmployeeOffersReport.service').AllEmployeeOffersReportEntry} */ { requestTypeLabel }) => (
                                    <>
                                        <span className="text-md-semibold">
                                            {requestTypeLabel}
                                        </span>
                                    </>
                                )
                            }
                        </Cell>
                    </Column>
                    {!isMobile && (
                        <Column
                            flexGrow={1}
                            align="center"
                            key="initiator"
                            sortable
                        >
                            <HeaderCell>{tReport('initiator')}</HeaderCell>
                            <Cell dataKey="initiator">
                                {
                                    (/** @type {import('./AllEmployeeOffersReport.service').AllEmployeeOffersReportEntry} */ { initiator }) => (
                                        <span className="text-md-semibold">{initiator}</span>
                                    )
                                }
                            </Cell>
                        </Column>
                    )}
                    <Column
                        width={100}
                        minWidth={10}
                        align="center"
                        key="requestStartDate"
                        sortable
                    >
                        <HeaderCell>{tReport('date')}</HeaderCell>
                        <Cell dataKey="requestStartDateSort">
                            {
                                (/** @type {import('./AllEmployeeOffersReport.service').AllEmployeeOffersReportEntry} */ { requestStartDates }) => (
                                    <>
                                        <span className="text-md-semibold">
                                            {requestStartDates}
                                        </span>
                                    </>
                                )
                            }
                        </Cell>
                    </Column>

                    {!isMobile && (
                        <>
                            <Column
                                flexGrow={1.5}
                                align="center"
                                key="location"
                                sortable
                            >
                                <HeaderCell>{tReport('location')}</HeaderCell>
                                <Cell dataKey="location">
                                    {
                                        (/** @type {import('./AllEmployeeOffersReport.service').AllEmployeeOffersReportEntry} */ { location }) => (
                                            <>
                                                <span className="text-md-semibold">
                                                    {location}
                                                </span>
                                            </>
                                        )
                                    }
                                </Cell>
                            </Column>
                            <Column
                                flexGrow={1.1}
                                align="center"
                                key="shiftTime"
                                sortable
                            >
                                <HeaderCell>{tReport('shiftTime')}</HeaderCell>
                                <Cell dataKey="shiftTimeSort">
                                    {
                                        (/** @type {import('./AllEmployeeOffersReport.service').AllEmployeeOffersReportEntry} */ { shiftTime }) => (
                                            <>
                                                <span className="text-md-semibold">
                                                    {shiftTime}
                                                </span>
                                            </>
                                        )
                                    }
                                </Cell>
                            </Column>
                            <Column
                                flexGrow={1.7}
                                align="left"
                                key="segments"
                                sortable
                            >
                                <HeaderCell>{tReport('segments')}</HeaderCell>
                                <Cell dataKey="segmentsSort">
                                    {
                                        (/** @type {import('./AllEmployeeOffersReport.service').AllEmployeeOffersReportEntry} */ { segments }) => (
                                            <>
                                                <span className="text-md-semibold">
                                                    {segments}
                                                </span>
                                            </>
                                        )
                                    }
                                </Cell>
                            </Column>
                        </>
                    )}
                    <Column
                        flexGrow={0.5}
                        align="center"
                        key="rulesValidationComplicance"
                        sortable
                    >
                        <HeaderCell >
                            <FontAwesomeIcon icon={faCheckCircle} />
                        </HeaderCell>
                        <Cell dataKey="rulesValidationComplicance">
                            {
                                (/** @type {import('./AllEmployeeOffersReport.service').AllEmployeeOffersReportEntry} */ rowData) => (
                                    <span className="text-md-semibold">
                                        <OfferCompliance isCompliant={rowData.rulesValidationComplicance} />
                                    </span>
                                )
                            }
                        </Cell>
                    </Column>
                </Table>
                {isMobile && showDetails && selectedRow && (
                    <OfferDetails
                        data={selectedRow}
                        onClose={handleCloseDetails}
                    />
                )}
            </Paper>
        </div>
    );
};

export default AllEmployeeOffersReport;
